import { FeatureName, useIsFeatureEnabled } from '@gripp/shared-logic';
import { ColumnData, ListCountProvider, PanelShell } from '@gripp/shared-ui';
import { useCallback, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Activities as ActivitiesOld } from './activities-old';
import { ActivityNew } from './activities-new';
import { ActivityFilters } from './activityFilters';
import { ActivityFoldOneColumn } from './activityFoldOneColumn';

type ActivitiesLocationState = {
  skipAutoSelect?: boolean;
};

type ActivityRow = {
  id: string;
};

export const Activities = () => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const location = useLocation();

  if (isCropCircleEnabled) {
    return <ActivitiesBase />;
  }

  if (location.pathname.replace(/\/$/, '') === '/activities/filters') {
    return <Navigate to="/activities" replace />;
  }

  if (location.pathname.replace(/\/$/, '') === '/activities/new') {
    return <ActivityNew />;
  }

  return <ActivitiesOld />;
};

const ActivitiesBase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { issueId } = useParams();
  const [count, setCount] = useState<number>();
  const skipAutoSelect = (location.state as ActivitiesLocationState | null)
    ?.skipAutoSelect;
  const isActivityRoot = location.pathname.replace(/\/$/, '') === '/activities';

  const onIssueSelected = useCallback(
    (id: string) => {
      navigate(`/activities/${id}`);
    },
    [navigate]
  );

  const handleDataLoaded = useCallback(
    (data: ActivityRow[]) => {
      const firstIssueId = data?.[0]?.id;
      if (firstIssueId && !issueId && !skipAutoSelect && isActivityRoot) {
        onIssueSelected(firstIssueId);
      }
    },
    [isActivityRoot, issueId, onIssueSelected, skipAutoSelect]
  );

  const handleFilterChange = useCallback(() => {
    navigate('/activities/filters', { state: { skipAutoSelect: true } });
  }, [navigate]);

  const leftColumn: ColumnData = {
    title: 'activity.menu.title',
    count,
    mainComponent: (
      <ActivityFoldOneColumn
        onIssueSelected={onIssueSelected}
        showAssetName={true}
        selectedIssue={issueId}
        onDataLoaded={handleDataLoaded}
        onActivityCountChange={setCount}
        hideFilterBar={true}
      />
    ),
    onBackButtonPress: () => {
      navigate('/activities', { state: { skipAutoSelect: true } });
    },
    headerComponent: <ActivityFilters onFilterChange={handleFilterChange} />,
    disableScroll: true,
  };

  return (
    <ListCountProvider count={count}>
      <PanelShell leftColumn={leftColumn} />
    </ListCountProvider>
  );
};
